// Черновик вакансии. Панель, а не модалка: черновик правится и удаляется, необратимого в нём
// нет (правило 4). Необратимое начинается на кнопке «Разместить» в карточке записи — там
// объявление уходит на площадку и занимает город.
//
// ## Почему место работы стоит отдельным полем, а не спрятано в текст
//
// Площадка не пропускает один и тот же текст в двух городах, если у объявлений не указано
// место работы (тз 6.1, проверка живёт на сервере). Заказчик набирает охрану сразу в несколько
// городов одним и тем же текстом — то есть без этого поля второе объявление просто не
// разместится, а за повтор площадка блокирует учётную запись, вплоть до безвозвратной.
//
// ## Сценарий 107: дубль ловит сервер, а не форма
//
// Отдельного маршрута предпроверки дубля в контракте нет, и выдумывать проверку на клиенте
// нельзя: она врала бы ровно в том случае, ради которого нужна, — когда второе объявление
// заводит другой человек в другом окне. Правило живёт на сервере (частичный уникальный индекс
// плюс понятный отказ) и приезжает конвертом ошибки при размещении. Здесь остаётся
// предупредить об этом ДО того, как человек наберёт объявление, — чтобы отказ не выглядел
// внезапным после десяти минут работы.

import type { FormEvent } from 'react';
import { useState } from 'react';

import { RiBriefcase4Line, RiMapPin2Line } from '@remixicon/react';

import * as Alert from '@/shared/ui/alert';
import * as Button from '@/shared/ui/button';
import { ErrorAlert } from '@/shared/ui/error-alert';
import { SelectField } from '@/shared/ui/select-field';
import { TextArea } from '@/shared/ui/text-area';
import { TextField } from '@/shared/ui/text-field';

import type { KeyKind } from '../api/platform';
import { GateNote, NoPermissionNote, useCan } from '../components/gates';
import { newIdempotencyKey, useAccountsQuery, useCreateVacancyMutation } from '../queries';

/** Вакансии ведёт рекрутёр: у него пакет размещения (матрица тз, раздел 3.2). */
const PUBLISH_PERMISSION = 'hiring.avito.publish';
const PUBLISH_OWNER = 'Вакансии на площадке ведёт рекрутёр — попросите его завести объявление.';

const ЗАПАСНОЕ_ИМЯ: Readonly<Record<KeyKind, string>> = {
  company: 'Ключ компании',
  employee: 'Ключ сотрудника',
};

const ЧЕМ_ВЕДЁМ: Readonly<Record<KeyKind, string>> = {
  employee:
    'Пакет размещения на площадке привязан к сотруднику, а не к компании: обычно вакансии ведут именно этим ключом.',
  company:
    'Ключом компании объявление тоже разместится, но пакет размещения не у него — платные места кончатся быстрее.',
};

const ВИДЫ: readonly KeyKind[] = ['employee', 'company'];

function этоВидКлюча(значение: string): значение is KeyKind {
  return значение === 'company' || значение === 'employee';
}

type Поле = 'title' | 'body' | 'city';

export function NewVacancyPanel({
  accountId,
  onClose,
}: {
  accountId: string;
  onClose: () => void;
}) {
  const allowed = useCan(PUBLISH_PERMISSION);
  const accounts = useAccountsQuery();
  const account = accounts.data?.items.find((item) => item.id === accountId);

  // Ключ идемпотентности живёт столько же, сколько форма: двойное нажатие и повтор после
  // обрыва вернут тот же черновик, а не заведут второй с тем же текстом.
  const [ключ] = useState(newIdempotencyKey);

  const [название, setНазвание] = useState('');
  const [текст, setТекст] = useState('');
  const [город, setГород] = useState('');
  const [регион, setРегион] = useState('');
  const [место, setМесто] = useState('');
  // Умолчание — ключ сотрудника: пакет размещения привязан к сотруднику (тз 6.1).
  const [видКлюча, setВидКлюча] = useState<string>('employee');
  const [ошибки, setОшибки] = useState<Partial<Record<Поле, string>>>({});
  const [отказ, setОтказ] = useState<string | null>(null);

  const mutation = useCreateVacancyMutation(ключ);

  const варианты = ВИДЫ.map((kind) => ({
    value: kind,
    label:
      account?.credentials.find((набор) => набор.kind === kind)?.kind_title ?? ЗАПАСНОЕ_ИМЯ[kind],
  }));

  function отправить(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!allowed) {
      setОтказ(`Права заводить вакансии у вас нет. ${PUBLISH_OWNER}`);
      return;
    }
    if (mutation.isPending) {
      setОтказ('Черновик уже создаётся, ждём ответ сервера. Второй запрос не отправляем.');
      return;
    }

    const найдено: Partial<Record<Поле, string>> = {};
    if (название.trim() === '') {
      найдено.title = 'Введите название: его человек видит на площадке первым.';
    }
    if (текст.trim() === '') {
      найдено.body = 'Введите текст объявления — без него размещать нечего.';
    }
    if (город.trim() === '') {
      найдено.city = 'Укажите город: площадка размещает объявление в конкретном городе.';
    }
    if (Object.keys(найдено).length > 0) {
      setОшибки(найдено);
      return;
    }

    setОшибки({});
    setОтказ(null);
    mutation.mutate(
      {
        account_id: accountId,
        title: название.trim(),
        body: текст.trim(),
        city: город.trim(),
        region: регион.trim() === '' ? null : регион.trim(),
        workplace: место.trim() === '' ? null : место.trim(),
        key_kind: этоВидКлюча(видКлюча) ? видКлюча : 'employee',
      },
      { onSuccess: onClose },
    );
  }

  return (
    <form onSubmit={отправить} noValidate className='flex flex-col gap-3'>
      {!allowed && <NoPermissionNote что='заводить вакансии на площадке' кто={PUBLISH_OWNER} />}

      {/* Предупреждение стоит ДО полей, а не после отказа сервера: человек, набравший
          объявление и получивший «такое уже стоит в этом городе», переписывает текст заново.
          Узнать про правило до набора дешевле в десять раз. */}
      <Alert.Root variant='light' status='warning' size='small' role='status'>
        <Alert.Icon as={RiBriefcase4Line} />
        <span>
          Одинаковый текст в одном городе площадка не примет — даже со второй учётной записи, и
          блокирует за это. В разных городах тот же текст допускается, но только если у каждого
          объявления указано место работы.
        </span>
      </Alert.Root>

      <TextField
        id='new-vacancy-title'
        label='Название вакансии'
        required
        value={название}
        onChange={setНазвание}
        placeholder='Например: охранник на объект, вахта'
        icon={RiBriefcase4Line}
        maxLength={255}
        error={ошибки.title}
      />

      <TextArea
        id='new-vacancy-body'
        label='Текст объявления'
        required
        value={текст}
        onChange={setТекст}
        rows={8}
        placeholder='Условия, график, требования, что предоставляем'
        error={ошибки.body}
        hint='Именно по этому тексту площадка узнаёт повтор объявления, а не по названию.'
      />

      <TextField
        id='new-vacancy-city'
        label='Город'
        required
        value={город}
        onChange={setГород}
        placeholder='Например: Сургут'
        icon={RiMapPin2Line}
        maxLength={255}
        error={ошибки.city}
      />

      <TextField
        id='new-vacancy-region'
        label='Регион'
        value={регион}
        onChange={setРегион}
        placeholder='Например: Ханты-Мансийский автономный округ'
        maxLength={255}
        hint='Нужен там, где город с таким названием на площадке не один.'
      />

      <TextField
        id='new-vacancy-workplace'
        label='Место работы'
        value={место}
        onChange={setМесто}
        placeholder='Например: база на Нефтеюганском шоссе'
        maxLength={255}
        hint='Без места работы площадка не пропустит такой же текст во втором городе: она сочтёт объявления повтором. Если этот текст стоит только в одном городе, поле можно оставить пустым.'
      />

      <SelectField
        id='new-vacancy-key'
        label='Каким ключом вести'
        required
        value={видКлюча}
        onChange={setВидКлюча}
        options={варианты}
        placeholder='Выберите набор доступов'
        // `pending` тут НЕ ставится, хотя названия наборов и приезжают с сервера. Выбор
        // фиксированный — ключ компании либо ключ сотрудника, — и подпись «Загружаем
        // справочник…» на нём обещала бы список, которого нет: пока запись едет, названия
        // берутся запасные, а сам выбор доступен сразу.
        hint={этоВидКлюча(видКлюча) ? ЧЕМ_ВЕДЁМ[видКлюча] : undefined}
      />

      {/* Отказ по дублю приезжает отсюда, конвертом сервера: в нём уже названы и город, и
          учётная запись, под которой стоит прежнее объявление, и следующий шаг. */}
      {mutation.isError && <ErrorAlert error={mutation.error} />}

      {отказ !== null && <GateNote>{отказ}</GateNote>}

      <div className='flex items-center gap-2 pt-1'>
        <Button.Root type='submit' variant='primary' mode='filled' size='small'>
          {mutation.isPending ? 'Сохраняем черновик…' : 'Сохранить черновик'}
        </Button.Root>
        <Button.Root type='button' variant='neutral' mode='stroke' size='small' onClick={onClose}>
          Отмена
        </Button.Root>
      </div>
    </form>
  );
}
